import { DataService } from './service/data.service';
import { AppConfigService } from 'src/app/config/config.service';
import { BusinessAssociation, LenderConfiguration } from '@shared/model/lender-config.model';
import { isPlatformBrowser, PlatformLocation /* CommonModule */ } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  DOCUMENT,
  PLATFORM_ID,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivationStart, ActivatedRoute, Params /* RouterModule */ } from '@angular/router';
import {
  DictionaryService,
  ThemeService,
  TokenService,
  ToastService,
  AppConfigService as ConfigService,
  ContactType
  // SharedUiLibraryModule,
  // GlobalHeaderLibraryModule
} from '@cudirect/shared-ui-library';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LegalService } from 'src/legal/legal.service';
import { RoutingUrl } from 'src/shared/constant/route-const';
import { UserSessionService } from 'src/shared/service/user-session.service';
import { ChatIntegrationService } from '@shared/service/chat-integration.service';
import { AppConfig } from './config/config.type';
import pkg from 'package.json';
import { TimerService } from '@shared/service/timer.service';
import { VersioningService } from './service/versioning.service';
import { StatefulService } from './service/stateful.service';
import { IframeService } from './service/iframe.service';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { NotificationConstant } from '@workflow/ecp-shared/constant/notification.const';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { PageInstance } from '@workflow/class/page.service';
import { DevtoolsBridgeService } from '@shared/service/devtools/devtools-bridge.service';
// import { SharedModule } from '@shared/shared.module';
import { RuntimeConfigService } from './service/runtime-config.service';

/**
 * Component representing app component section
 * @Component
 */
@Component({
  selector: 'cu-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  // NG19 change to to true when migrating to NG19 is completed, then move to bootstrapApplication(AppComponent) in main.ts
  standalone: false
  // imports: [CommonModule, RouterModule, SharedUiLibraryModule, SharedModule, GlobalHeaderLibraryModule]
})
/**
 *
 * Class representing app component section
 * @export
 * @class AppComponent
 * @implements {AfterViewChecked}
 * @implements {OnInit}
 */
export class AppComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  /** Workflow page */
  public page: PageInstance;
  /** Return ready status */
  public ready = false;
  /** Return title name */
  public title = 'MyOri';
  /** Lender Data coming from local lenderConfig */
  public lenderConfig: LenderConfiguration;
  /** Client context for chat widget */
  /** comment  for chat widget */
  // public chatWidgetContext: any;
  public showUserSession = false;
  // Active Business Contact Associations
  public activeBusinessAssociations: BusinessAssociation[] = [];

  /** Version of ECP.WEB / arcDX / DOP - Leveraged for Cache Busting */
  public version = pkg.version;

  public userAuthenticated = false;
  private subscriptions: Subscription[] = [];

  @HostBinding('class.bl-iframe')
  public frame = false;

  // Is this the Admin Portal?
  public isAdmin = false;

  // Hide Footer
  public hideFooter = false;

  public readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * For PWA Manifest file
   */
  public siteUrl = this.isBrowser ? window.location.origin : '';

  /**
   * Get the app config data from config.json
   */
  public appConfigData: AppConfig;
  private elRef: HTMLBodyElement;
  private networkStatus$: Subscription = Subscription.EMPTY;

  /**
   * True when resume banner should be shown;
   */
  public showResumeBanner = false;
  private dictionarySub: Subscription;
  public showContactUsPanel = false;

  // Inject services using the modern inject pattern
  private readonly dictionary = inject(DictionaryService);
  private readonly legalService = inject(LegalService);
  private readonly lenderService = inject(LenderService);
  private readonly themeService = inject(ThemeService);
  private readonly toast = inject(ToastService);
  private readonly location = inject(PlatformLocation);
  private readonly router = inject(Router);
  private readonly subscribeEmitService = inject(SubscriptionEmitService);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly workflowService = inject(WorkflowService);
  private readonly userService = inject(UserService);
  private readonly userSessionService = inject(UserSessionService);
  private readonly titleService = inject(Title);
  private readonly chatIntegrationService = inject(ChatIntegrationService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly configService = inject(ConfigService);
  private readonly document = inject(DOCUMENT);
  private readonly dataService = inject(DataService);
  private readonly tokenService = inject(TokenService);
  private readonly timerService = inject(TimerService);
  private readonly versioningService = inject(VersioningService);
  private readonly statefulService = inject(StatefulService);
  private readonly iframeService = inject(IframeService);
  private readonly renderer = inject(Renderer2);
  private readonly runtimeConfigService = inject(RuntimeConfigService);
  private readonly devtoolsBridge = inject(DevtoolsBridgeService);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    this.dictionary.path = '/assets/dictionary'; // relative or absolute path
    this.dictionary.file = 'dictionary'; // all component and language flavors merge to here

    // Define Dictionary Overrides at the App and Lender level
    const lenderOverrides = this.lenderService.lenderData?.dictionaryOverride ? this.lenderService.lenderData?.dictionaryOverride : null;
    let appOverrides = {
      'ea-authenticate-create': {
        errors: {
          failedDeferredCreate: {
            detail: 'Please make sure your information matches what was entered on your application'
          }
        }
      }
    };

    // If lender override strings are present, lets add them
    if (typeof lenderOverrides === 'string') {
      const jsonOverrides = JSON.parse(lenderOverrides);
      // Merge lender with App Overrides
      appOverrides = { ...appOverrides, ...jsonOverrides };
    }
    // Override strings in the dictionary
    this.dictionary.jsonOverride = appOverrides;

    // THEME SERVICE CONFIGURATION OPTIONS
    // Override theme service defaults
    this.themeService.defaultLogo = '';
    this.themeService.testFallbackLogo = false;
    this.themeService.enableLocalStorageTesting = false;
    this.themeService.enablePWAMode = true;
    this.themeService.manifestValues = {
      scope: this.siteUrl + '/',
      start_url: this.siteUrl + '/',
      name: this.lenderService?.lenderData?.lenderName,
      short_name: this.lenderService?.lenderData?.lenderName,
      description: 'Borrower Portal for ' + this.lenderService?.lenderData?.lenderName,
      icon192: 'assets/images/pwa-icons/icon-192.png',
      icon512: 'assets/images/pwa-icons/icon-512.png'
    };

    if (this.isBrowser) {
      this.tokenService.configureCache(this.configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
      this.elRef = this.document.getElementsByTagName('body')[0];

      // Init dictionary service w/ undefined (path/file defined above) and a version for cachebusting
      this.dictionary.load(undefined, Date.now().toString());
      this.dictionarySub = this.dictionary.onLoad.subscribe(() => {
        this.dictionarySub.unsubscribe();
        this.dataService.dictionaryIsReady$.next(true);
      });
    }
  }

  @HostListener('window:popstate', ['$event'])
  public onPopState(): void {
    // Handle popstate event here
    this.showUserSession = false;
  }

  // TODO: This is a bad way to set the title, it should be pulled from meta data from the route object
  public pageTitle(): string {
    const dictionary = this.dictionary;
    const dict = (value: string): string => dictionary.read('pageTitles/' + value);

    // Admin Portal
    if (this.isPath('admin/')) {
      return dict('adminPortal');
    }
    // User page titles
    if (this.isPath('user/login')) {
      return dict('userLogin');
    }
    if (this.isPath('user/verify')) {
      return dict('userVerify');
    }
    if (this.isPath('user/registration', 'user/light-registration')) {
      return dict('userRegistration');
    }
    if (this.isPath('user/profile')) {
      return dict('userProfile');
    }
    // Post application titles
    if (this.isPath('/dashboard')) {
      return dict('dashboard');
    }
    if (this.isPath('postapp/overview')) {
      return dict('portalOverview');
    }
    if (this.isPath('postapp/tasks')) {
      return dict('portalDocuments');
    }
    if (this.isPath('postapp/document')) {
      return dict('portalDocuments');
    }
    if (this.isPath('postapp/activity')) {
      return dict('portalActivity');
    }
    // Legal pages titles
    if (this.isPath('legal/terms-of-use')) {
      return dict('legalTermsOfUse');
    }
    if (this.isPath('legal/privacy-policy')) {
      return dict('legalPrivacyPolicy');
    }
    if (this.isPath('terms-and-policy')) {
      return dict('origenceTermsPolicy');
    }
    // Workflow application pages (default using page h1 tag)
    if (this.page && this.page.customTitleOrDefault) {
      return this.page.customTitleOrDefault;
    }

    // Global error/contact us page
    if (this.isPath('/oops')) {
      return dict('contactUs');
    }
  }

  private checkNetworkStatus() {
    if (this.isBrowser) {
      this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
        .pipe(map(() => navigator.onLine))
        .subscribe((status) => {
          if (status) {
            if (this.appConfigService.getNetworkStatus() === false) {
              this.showToaster('You are back online...', 2000);
            }
          } else {
            this.showToaster('You are offline. Please reconnect...', 60000);
          }
          this.appConfigService.setNetworkStatus(status);
        });
    }
  }

  /**
   * Show toaster message by invoking share library
   * @param text
   */
  private showToaster(text: string, timeout: number = 5000) {
    this.toast.open(this.toastHtml(text), timeout, `<span></span>`, 'burnt-toast').subscribe(() => {});
  }

  /**
   * Toaster text
   * @param text
   * @returns
   */
  private toastHtml(text: string): string {
    const html = `<span class="bl-font-weight--800 bl-font-color--white">${text}</span> `;
    return html;
  }

  /**
   * Since ng14, this is provided by the router, but we're not using it yet
   * https://v14.angular.io/guide/router#setting-the-page-title
   */
  private setPageTitle(): void {
    // Adds null check for lender name, as during implementation, we may not have a lender name set
    // Will remove a lot of unnecessary errors in the console in UAT/PROD
    const lenderName = this.lenderConfig?.lenderName ? this.lenderConfig.lenderName : 'No Lender Name Set';
    const spacer = ' - ';
    const pageTitle = this.pageTitle() ? this.pageTitle() + spacer + lenderName : lenderName;
    this.titleService.setTitle(pageTitle);
  }

  // Pass in 1 or more url path strings to check if
  // current location contains provided string(s)
  public isPath(...pathnames: string[]): boolean {
    const currentPath = this.location.pathname;
    return currentPath && pathnames.some((pathname) => currentPath.toLowerCase().includes(pathname.toLowerCase()));
  }

  /**
   * Determines if were in Admin Portal or not by path
   * @returns boolean
   */
  public adminPages(): boolean {
    this.isAdmin = this.isPath('/admin');
    return this.isAdmin;
  }

  /**
   * Hide Footer & ReCaptcha for Admin Pages & Esign iframe
   */
  public setHideFooterFlag(): void {
    this.hideFooter = this.isAdmin || this.isPath('/document-esign');
  }

  /** property to hide header for esign call back pages */
  public get hideHeader(): boolean {
    return (
      this.frame || (!this.router.url.includes('/integration/instatouch') && this.router.url.includes('/integration/')) || this.adminPages()
    );
  }

  // TECH: This should only be true on 1 condition, when the user is authenticated or not.
  // We dont need this function to evaluate on path
  public showHeaderUserDropdown(): boolean {
    return !this.isPath('user/login', 'user/registration', 'user/light-registration') && this.userService.isExistingUser;
  }

  private startSessionOnInit(): void {
    this.userSessionService.startTimer();
  }

  // NOTE: would like to move this to postapp/components/document-esign.component.ts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processESignResponse(response: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.subscribeEmitService.emit<any>(NotificationConstant.ESign.ESign_Subscription, response);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processLocalStorageResponse(response: any): void {
    if (!response) {
      return;
    }
    // do what you want on logout event
    if (
      response.key === NotificationConstant.LocalStorage.LogOut_Event &&
      this.router.url !== RoutingUrl.Login &&
      !this.router.url.includes('/admin')
    ) {
      this.router.navigate([RoutingUrl.Login]);
    }
  }

  /**
   *
   * Initilize while page load
   * @memberof AppComponent
   */
  public ngOnInit(): void {
    const appIsIframed = this.isBrowser ? window.self !== window.top : false;
    if (this.isBrowser) {
      this.devtoolsBridge.init();
    }

    if (this.isBrowser && appIsIframed) {
      this.iframeService.init();
    }

    this.checkNetworkStatus();
    // Grab the data from config.json
    this.appConfigData = this.runtimeConfigService.getConfig;
    this.setFrameFlag();

    const queryParamSub = this.activeRoute.queryParams.subscribe((params) => {
      if (this.isBrowser) {
        this.storeParamtoSession(params);
      }
    });
    this.subscriptions.push(queryParamSub);

    // Set workflow page
    this.page = this.workflowService.currentPage;

    // Set Lender Data (API)
    this.lenderConfig = this.lenderService.lenderDataInfo;
    this.activeBusinessAssociations = this.lenderConfig?.businessAssociations?.filter((f) => f?.enabled === true);

    if (this.isBrowser && !this.adminPages() && !appIsIframed) {
      // Initialize chat provider (or none) - no FF here because it is comes from LC
      // NOTE: We are aware that lenders who iframe into hb sites will *not* have chat - the hb site should manage its own instance of chat
      // NOTE: this.router.routerState.snapshot.url is not behaving at init time & PlatformLocation is not supposed to be used (above)
      // TODO: Down the road, we may need to implement logic to add/remove the snippet on route change - currently, it's rudiementary (onload)
      // TODO: Down the road, we may want to move the route ignore logic into the service, and make it more elegant (pass routes to method)
      this.chatIntegrationService.initialzeChatProvider(this.lenderConfig?.chatIntegration);
    }

    this.setHideFooterFlag();

    const routerEventSub = this.router.events.subscribe((event) => {
      this.setPageFocus(event);
    });
    this.subscriptions.push(routerEventSub);

    if (this.isBrowser) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.subscribeEmitService.register<any>(NotificationConstant.Asset.Finicity_Subscription);

      // NOTE: would like to move this to postapp/components/document-esign.component.ts
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.subscribeEmitService.register<any>(NotificationConstant.ESign.ESign_Subscription);

      const timerSub = this.userSessionService.timerEvent.subscribe((action) => {
        this.handleTimerEvent(action);
      });
      this.subscriptions.push(timerSub);

      // Idile popup clock
      if (!this.isPath('/admin')) {
        this.startSessionOnInit();
      }

      // TODO: Why are we checking this here?
      this.checkUsertoken();

      // Check for version updates periodically
      this.versioningService.startVersionCheck();

      // Capture app init duration in Datadog
      this.timerService.endTimerAndMeasure('app-init-oninit', 'ecp.ui.app-init-duration');
    }
  }

  /**
   * Check the url has frame to set flag
   * @returns true / false
   * @remarks
   * TODO: Consolidate this with the iframe service
   */
  private setFrameFlag(): void {
    // First check if 'iframe' is in session storage, if not, set to false
    let frameFromSession = false;

    if (this.isBrowser) {
      const storedIframeValue = sessionStorage.getItem('iframe');
      frameFromSession = storedIframeValue ? !storedIframeValue : false;
    }

    this.frame = frameFromSession;

    // Grab the query string, if present
    const location = this.location.search?.toLowerCase();
    const isIframe = location.includes('frame=yes') || location.includes('frame=true');

    // If its present in the query string, use that, else persist the value from above.
    this.frame = this.frame || isIframe;

    // Save to session storage
    if (this.isBrowser) {
      sessionStorage.setItem('iframe', isIframe.toString());
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isValidContactType(type: any): type is ContactType {
    return ['phone', 'sms', 'email', 'url'].includes(type.toLowerCase());
  }

  // Might need to moved to a shared service, utilities file
  public generateContactUrl(contactType: ContactType, contactValue: string): string {
    // contactType = contactType as ContactType;
    let urlPrefix = '';

    if (contactType) {
      switch (contactType.toLowerCase()) {
        case 'phone':
        case 'sms':
          urlPrefix = 'tel:';
          break;
        case 'email':
          urlPrefix = 'mailto:';
          break;
      }
    }
    return urlPrefix + contactValue;
  }

  /**
   * Sets the page focus to the header on page load, per ADA/WCA requirements
   * @param event - Event
   */
  private setPageFocus(event): void {
    if (!this.isBrowser) {
      return;
    }
    if (event instanceof ActivationStart) {
      const element = this.document.getElementById('global-header');
      if (element && typeof (element as HTMLElement).scrollIntoView === 'function' && !event.snapshot.fragment) {
        (element as HTMLElement).scrollIntoView();
      }
    }
  }

  /**
   * Function to check if the user token is valid
   */
  private checkUsertoken(): void {
    if (this.userService.UserInfo?.token) {
      const token = this.userService.UserInfo.token;
      const auth = this.statefulService.wrap('ss:authorization').valueString;
      const ola_auth = this.statefulService.wrap('ss:auth-ola').valueString;
      if (token !== auth && token !== ola_auth && token && !this.isPath('/legal', '/admin', '/integration')) {
        this.userSessionService.logOut(true);
      }
    }
  }

  /**
   * Function to handle timer event, pulled out of ngOnInit for readability, SQ code smell
   * @param action - string
   */
  private handleTimerEvent(action: string): void {
    switch (action) {
      case 'show':
        if (this.router.routerState.snapshot.url.includes('/admin')) {
          // User is in AP, not ECP. Don't show modal, just log them out.
          this.userSessionService.logOut(false);
        } else {
          this.showUserSession = true;
        }
        break;
      case 'logout':
        this.userSessionService.logOut(true, true);
        this.showUserSession = false;
        break;
      case 'hide':
        this.showUserSession = false;
        break;
      default:
    }
  }

  /**
   *
   * Initilize after page load
   * @memberof AppComponent
   */
  public ngAfterViewInit(): void {
    if (this.isBrowser) {
      // E-Sign Integration
      // TODO: do we need this at a global level?
      // NOTE: would like to move this to postapp/components/document-esign.component.ts
      window.addEventListener(NotificationConstant.ESign.ESign_Event, this.processESignResponse.bind(this));

      // listen to storage event
      // TODO: Do we need this at all, and a global level? What are we listening for?
      window.addEventListener(NotificationConstant.LocalStorage.Window_Event_Name, this.processLocalStorageResponse.bind(this));

      // Mesaure afterview timing to diff against oninit timing - we want this to come as late as possible in the app load lifecycle
      this.timerService.endTimerAndMeasure('app-init-afterview', 'ecp.ui.app-viewload-duration');

      // Need to do this a better way, like dynamically injecting recaptcha instead disabling it after the fact if disabled.
      if (!this.lenderService.lenderData?.enableRecaptcha || this.hideFooter) {
        this.renderer.addClass(this.document.body, 'recaptcha-disabled');
      }
    }
  }

  /**
   *
   * Initilize while after view rendered
   * @memberof AppComponent
   */
  public ngAfterViewChecked(): void {
    if (this.page?.id !== this.workflowService?.currentPage?.id) {
      this.page = this.workflowService.currentPage;
    }
    // Set page title
    this.setPageTitle();
  }

  private storeParamtoSession(queryParams: Params) {
    if (!this.isBrowser || this.userService.isAuthenticated) {
      return;
    }

    const params = [
      'autoredirect',
      'oidcid',
      'loantype',
      'loantypeoption',
      'target',
      'otutoken',
      'continue',
      'promocode',
      'MembershipType',
      'IsCUEmployee'
    ];
    // Fix to case sensitive issue with reading query params.
    const queryKeys = Object.keys(queryParams);
    params.forEach((p) => {
      const queryParamKey = queryKeys.find((t) => t.toLowerCase() === p.toLowerCase());
      if (queryParamKey && queryParams?.[queryParamKey]) {
        sessionStorage.setItem(p, queryParams[queryParamKey]);
      }
    });
  }

  public ngOnDestroy(): void {
    this.networkStatus$.unsubscribe();
    if (this.isBrowser) {
      window.removeEventListener(NotificationConstant.LocalStorage.Window_Event_Name, this.processLocalStorageResponse.bind(this));
    }

    if (this.isBrowser) {
      this.subscribeEmitService.unRegister(NotificationConstant.Asset.Finicity_Subscription);
      // NOTE: would like to move this to postapp/components/document-esign.component.ts
      this.subscribeEmitService.unRegister(NotificationConstant.ESign.ESign_Subscription);
    }
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }

  /**
   * Called when user clicks continue on session warning modal. The click will reset the timer automatically.
   */
  public continueSession(): void {
    this.showUserSession = false;
  }

  /**
   * Called when user clicks logout on session warning modal or user times out. Logout is handled from the session modal component.
   * This just closes modal.
   */
  public handleLogoutClick(): void {
    this.showUserSession = false;
  }
}
